/**
 * WorkspaceChrome — shared header parts for full-bleed workspace overlays (Billing, CRM,
 * Roadmap & Bugs, Pulse, ...).
 *
 * Before this, each workspace hand-rolled its own copy of the header: an identical
 * background/border/height style object, an identical hamburger button + inline SVG, an
 * identical title style, and (in three of them) a byte-for-byte identical tab-pill function —
 * five separate copies that had already started drifting apart (a lighter "elevated" header
 * background in three of them vs. the page-colored one in Pulse, a stronger border in Pulse's
 * own copy). Import the pieces below instead of re-declaring them so every workspace theme
 * change happens once.
 *
 * The header intentionally mirrors the Projects outline's own sticky chrome
 * (#aj-sc-chrome / .wf-chrome in outline.css): `--aj-bg-chrome` is the same value as
 * `--aj-bg-page` in both themes, so the bar never reads as a separate colored strip — and
 * MenuButton reuses the outline engine's own `.wf-nav-btn` / `.wf-sitenav-btn` CSS classes
 * directly, so the hamburger icon has identical weight/color/hover in both chromes and can
 * never drift out of sync again.
 */

export function MenuButton() {
  return (
    <button
      className="wf-nav-btn wf-sitenav-btn"
      title="Menu"
      onClick={() => window.dispatchEvent(new CustomEvent('toggle-site-nav'))}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="3.5" width="14" height="1.5" rx="0.75" fill="currentColor" />
        <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor" />
        <rect x="1" y="11" width="14" height="1.5" rx="0.75" fill="currentColor" />
      </svg>
    </button>
  )
}

/** Same vertical hairline the outline chrome uses between its own nav controls. */
export function ChromeDivider() {
  return <span className="wf-chrome-div" />
}

// Right padding is wider than left: NotificationBell.jsx renders a fixed 30px button at
// right:12 (plus its unread badge overhanging a few px further), always on top (z-index 500)
// of every full-bleed workspace. Without this clearance, a workspace's own rightmost header
// button collides with/gets visually clipped by the bell (confirmed on Tracker + Studio
// Calendar in production) — 54px keeps the bell's full footprint clear with margin to spare.
export const workspaceHeaderStyle = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '0 54px 0 18px', height: 48, flexShrink: 0,
  borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-chrome)',
}

export const workspaceTitleStyle = {
  fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 12,
  letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text)',
}

/** Pill-group wrapper for a Table/Board- or Readiness/Run-style header toggle. */
export const workspaceTabsWrapStyle = { display: 'flex', gap: 2, background: 'var(--aj-bg-muted)', borderRadius: 7, padding: 2 }

export function workspaceTabStyle(on) {
  return on
    ? { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: 'none', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }
    : { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: 'none', borderRadius: 5, padding: '4px 12px', cursor: 'pointer' }
}

/**
 * WorkspaceHeader — the one header ROW every full-bleed workspace uses (2026-09-04, the
 * workspace mobile pass). Before this, each of the 6 workspaces built on `workspaceHeaderStyle`
 * (Billing, CRM, Studio, Pulse/Team, Tracker, Scheduler — Templates is a centered modal, not one
 * of these, and was left alone) hand-assembled the SAME three clusters — menu+title(+an optional
 * inline extra like CRM's prospecting pill), a tab-pill group, and a trailing actions group
 * (search box, buttons) pushed right via its own `marginLeft:'auto'` or a `flex:1` spacer — all
 * inline in one non-wrapping 48px row. That crammed onto a ~390px phone screen either overflowed
 * or crushed; there was no room for it to go.
 *
 * This component doesn't change what any workspace puts in those three slots — `tabs` and
 * `actions` are still built by each caller exactly as before (different tab-gating logic,
 * different buttons) — it only gives the THREE clusters real class hooks (`.aj-ws-head-main` /
 * `-tabs` / `-actions`) so one shared compact-viewport rule (outline.css, `@media (max-width:
 * 760px)`, mirroring the Flight Board mobile pass in docs/FLIGHT_PLAN.md "Round 39") can wrap
 * them: menu+title always stays on its own line; tabs scroll horizontally rather than wrap (CRM
 * alone can carry 6 tabs for an admin — stacking that many would eat the whole screen); actions
 * drop to a full-width row and their own input/button children shrink to fit instead of
 * overflowing. `!important` in that CSS is what lets it beat these components' own inline
 * `workspaceHeaderStyle` — the same trick Flight Board's own pass used — so nothing above the
 * breakpoint changes: a workspace not yet migrated to this component is unaffected, and a
 * migrated one renders byte-identical to before on desktop.
 */
export function WorkspaceHeader({ title, titleExtra, tabs, actions }) {
  return (
    <header className="aj-ws-header" style={workspaceHeaderStyle}>
      <div className="aj-ws-head-main">
        <MenuButton />
        <ChromeDivider />
        <span style={workspaceTitleStyle}>{title}</span>
        {titleExtra}
      </div>
      {tabs && <div className="aj-ws-head-tabs">{tabs}</div>}
      {actions && <div className="aj-ws-head-actions">{actions}</div>}
    </header>
  )
}
